import { Head, Link, usePage } from "@inertiajs/react"
import { ClipboardList, Coffee, Beaker, TrendingUp } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { OnboardingModal } from "@/components/onboarding-modal"
import AppLayout from "@/layouts/app-layout"
import { dashboardPath, cuppingsPath, samplesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface DashboardProps {
  user: {
    name: string
    email: string
    onboarding_dismissed: boolean
  }
  stats: {
    total_tastings: number
    total_samples: number
    total_ratings: number
    average_rating: number
  }
}

export default function Dashboard({ user, stats }: DashboardProps) {
  const { t } = useTranslation()
  const { organizations } = usePage().props
  const [onboardingOpen, setOnboardingOpen] = useState(false)

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("dashboard.title"),
      href: dashboardPath(),
    },
  ]

  useEffect(() => {
    if (
      (organizations as unknown[]).length === 0 &&
      !user.onboarding_dismissed
    ) {
      setOnboardingOpen(true)
    } else {
      setOnboardingOpen(false)
    }
  }, [organizations, user.onboarding_dismissed])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("dashboard.title")} />

      <div className="flex h-full flex-1 flex-col gap-8 p-6">
        {/* Welcome Header */}
        <div>
          <h1 className="text-4xl font-bold tracking-tight">
            {t("dashboard.welcome", { name: user.name })}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("dashboard.subtitle")}
          </p>
        </div>

        {/* Quick Action Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Link href={cuppingsPath()}>
            <Card className="glass hover:border-primary/50 cursor-pointer transition-colors">
              <CardContent className="flex flex-col items-center justify-center py-8">
                <div className="bg-primary mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                  <ClipboardList className="text-primary-foreground h-8 w-8" />
                </div>
                <h3 className="font-semibold">
                  {t("dashboard.quick_actions.cuppings.title")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("dashboard.quick_actions.cuppings.description")}
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link href="/cuppings/new">
            <Card className="glass hover:border-primary/50 cursor-pointer transition-colors">
              <CardContent className="flex flex-col items-center justify-center py-8">
                <div className="bg-primary mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                  <Coffee className="text-primary-foreground h-8 w-8" />
                </div>
                <h3 className="font-semibold">
                  {t("dashboard.quick_actions.start_cupping.title")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("dashboard.quick_actions.start_cupping.description")}
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link href={samplesPath()}>
            <Card className="glass hover:border-primary/50 cursor-pointer transition-colors">
              <CardContent className="flex flex-col items-center justify-center py-8">
                <div className="bg-primary mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                  <Beaker className="text-primary-foreground h-8 w-8" />
                </div>
                <h3 className="font-semibold">
                  {t("dashboard.quick_actions.samples.title")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("dashboard.quick_actions.samples.description")}
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Statistics Cards */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>{t("dashboard.stats.title")}</CardTitle>
            <p className="text-muted-foreground text-sm">
              {t("dashboard.stats.subtitle")}
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="border-border bg-card/50 flex items-center gap-4 rounded-lg border p-4">
                <div className="bg-primary flex h-12 w-12 items-center justify-center rounded-full">
                  <ClipboardList className="text-primary-foreground h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_tastings}</p>
                  <p className="text-muted-foreground text-sm">
                    {t("dashboard.stats.cuppings_created")}
                  </p>
                </div>
              </div>

              <div className="border-border bg-card/50 flex items-center gap-4 rounded-lg border p-4">
                <div className="bg-primary flex h-12 w-12 items-center justify-center rounded-full">
                  <Beaker className="text-primary-foreground h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_ratings}</p>
                  <p className="text-muted-foreground text-sm">
                    {t("dashboard.stats.samples_rated")}
                  </p>
                </div>
              </div>

              <div className="border-border bg-card/50 flex items-center gap-4 rounded-lg border p-4">
                <div className="bg-primary flex h-12 w-12 items-center justify-center rounded-full">
                  <TrendingUp className="text-primary-foreground h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {stats.average_rating.toFixed(1)}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {t("dashboard.stats.average_rating")}
                  </p>
                </div>
              </div>

              <div className="border-border bg-card/50 flex items-center gap-4 rounded-lg border p-4">
                <div className="bg-primary flex h-12 w-12 items-center justify-center rounded-full">
                  <Coffee className="text-primary-foreground h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_samples}</p>
                  <p className="text-muted-foreground text-sm">
                    {t("dashboard.stats.total_samples")}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <OnboardingModal open={onboardingOpen} onOpenChange={setOnboardingOpen} />
    </AppLayout>
  )
}
